import Box from '@mui/material/Box'
import { useRef, useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'

/** @type {Record<string, (url: string) => string | undefined } */
const sources = {
  youtube: (url) => {
    const id = extractYoutubeId(url)
    return `https://www.youtube.com/embed/${id}`
  },
  vimeo: (url) => {
    const videoId = url.substring(url.lastIndexOf('/'), url.length)
    return `https://player.vimeo.com/video${videoId}`
  },
  facebook: (url) => {
    return url
  },
}

//Copy pasta from pbxx2cp
const extractYoutubeId = (url) => {
  let id = ''
  url = url
    .replace(/(>|<)/gi, '')
    .split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/)
  if (url[2] !== undefined) {
    id = url[2].split(/[^0-9a-z_-]/i)
    id = id[0]
  } else {
    id = url
  }
  return id
}

const useStyles = makeStyles()(() => {
  return {
    iframeContainer: {
      border: 0,
      backgroundColor: '#000',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    iframe: {
      position: 'relative',
      width: '100%',
      height: '100%',
    },
  }
})

const facebookSdkSrc =
  'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.2'

/** @type {React.ComponentType<ExternalVideoProps>} */
const ExternalVideo = ({ url }) => {
  const { classes } = useStyles()
  const source = getVideoUrlSource(url)
  const parsedUrl = sources[source](url)

  const [loaded, setLoaded] = useState(!!window.FB)
  const facebookVideoRef = useRef(null)

  useEffect(() => {
    let script = document.createElement('script')
    script.src = facebookSdkSrc
    script.async = true

    const onScriptLoad = () => {
      setLoaded(true)
    }

    script.addEventListener('load', onScriptLoad)

    if (facebookVideoRef && facebookVideoRef.current) {
      if (loaded && !facebookVideoRef.current.getAttribute('fb-xfbml-state')) {
        window.FB.XFBML.parse()
        return
      } else if (!loaded) {
        document.body.appendChild(script)
      }
    }

    return () => {
      script.removeEventListener('load', onScriptLoad)
    }
  }, [loaded, facebookVideoRef])

  switch (source) {
    case 'facebook':
      return (
        <Box
          className={'fb-video'}
          data-allowfullscreen={'true'}
          data-href={url}
          ref={facebookVideoRef}
        />
      )
    case 'youtube':
    case 'vimeo':
      return (
        <Box className={classes.iframeContainer}>
          <iframe
            className={classes.iframe}
            src={parsedUrl}
            title={`${source}-iframe`}
          />
        </Box>
      )
    default:
      return null
  }
}

export const facebookRegex = new RegExp(
  /((facebook\.com\/?.+(watch|video).+)|(fb\.watch\/.+))/,
  'i',
)
export const vimeoRegex = new RegExp(
  /(?:vimeo\.com)\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|)(\d+)(?:|\/\?)/,
  'i',
)
export const youtubeRegex = new RegExp(
  /(?:youtube(?:-nocookie)?\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  'i',
)

export const getVideoUrlSource = (url) => {
  let source = null
  if (facebookRegex.test(url)) {
    source = 'facebook'
  } else if (vimeoRegex.test(url)) {
    source = 'vimeo'
  } else if (youtubeRegex.test(url)) {
    source = 'youtube'
  }
  return source
}

/**
 * @typedef {{
 *  url: string
 * }} ExternalVideoProps
 */

export default ExternalVideo
